import { cloneElement, memo, ReactNode, useCallback, useMemo } from 'react'
import cn from 'classnames'
import { ClickOutside } from 'components/helpers/ClickOutside'

import { CloseButton } from '../CloseButton'
import { TSize } from '../ModalDesktop'

interface IProps {
  children: ReactNode
  isCloseButton: boolean
  isCloseOnOutsideClick: boolean
  isOffsets: boolean
  size: TSize
  className?: string
  footer?: JSX.Element
  header?: JSX.Element
  id?: string
  isOrder?: boolean
  noPadding?: boolean
  onClose?: () => void
}

export const DialogDesktop = memo<IProps>(function DialogDesktop({
  children,
  className,
  footer,
  header,
  id,
  isCloseButton,
  isCloseOnOutsideClick,
  isOffsets,
  onClose,
  size,
  isOrder,
  noPadding
}) {
  const handleClickOutside = useCallback(() => {
    if (isCloseOnOutsideClick && onClose) {
      onClose()
    }
  }, [isCloseOnOutsideClick, onClose])

  const headerRender = useMemo(() => {
    const closeButton = isCloseButton ? <CloseButton onClick={onClose} /> : undefined

    return header ? cloneElement(header, { closeButton }) : null
  }, [header, isCloseButton, onClose])

  return (
    <ClickOutside
      data-component="modal"
      id={id}
      onClickOutside={handleClickOutside}
      className={cn('relative flex flex-col rounded-xl bg-surface', className, {
        'w-[448px] max-[496px]:w-full': size === 'sm',
        'w-[600px] max-[648px]:w-full': size === 'nm',
        'w-[844px] max-[892px]:w-full': size === 'lg',
        'w-[1004px] max-[1052px]:w-full': size === 'xl',
        'w-[62.5%] max-w-[1200px] rounded-[0] shadow-[0_12px_24px_rgba(0,0,0,0.24),0_8px_24px_rgba(0,0,0,0.12),0_0_1px_rgba(0,0,0,0.2)] max-[1680px]:w-[70%] max-[1263px]:w-[75%] max-[963px]:w-full':
          size === 'xxl',
        '[&_.body]:px-6 [&_.body]:py-0': isOffsets,
        '[&_.body]:pr-[64px]': isOffsets && isCloseButton,
        'overflow-y-auto': isOrder
      })}
    >
      {headerRender}
      <div
        className={cn('body', {
          'h-[calc(100%-72px)] !p-0': noPadding
        })}
      >
        {children}
      </div>
      {footer}
    </ClickOutside>
  )
})
